import { INavigationCapture } from '../../domain/interfaces/INavigationCapture';
import { LogEntry } from '../../domain/types';

export class NavigationCapture implements INavigationCapture {
  private callback: ((entry: LogEntry) => void) | null = null;
  private isActive = false;
  private originalPushState: typeof History.prototype.pushState;
  private originalReplaceState: typeof History.prototype.replaceState;
  private lastUrl = '';

  constructor() {
    this.originalPushState = History.prototype.pushState;
    this.originalReplaceState = History.prototype.replaceState;
    this.lastUrl = window.location.href;
  }

  start(callback: (entry: LogEntry) => void): void {
    if (this.isActive) return;
    
    this.callback = callback;
    this.isActive = true;
    
    this.captureInitialLoad();
    this.patchHistory();
    this.setupListeners();
  }

  stop(): void {
    if (!this.isActive) return;
    
    this.restoreHistory();
    this.removeListeners();
    this.callback = null;
    this.isActive = false;
  }

  private captureInitialLoad(): void {
    if (this.callback && document.readyState === 'complete') {
      this.callback({
        level: 'info',
        msg: `Page loaded: ${window.location.href}`,
        ts: new Date().toISOString(),
        source: 'browser',
        meta: {
          type: 'navigation',
          trigger: 'load',
          to: window.location.href,
          url: window.location.href
        }
      });
    }
  }

  private patchHistory(): void {
    const self = this;

    History.prototype.pushState = function(...args) {
      const result = self.originalPushState.apply(this, args);
      const newUrl = window.location.href;
      
      if (self.callback && newUrl !== self.lastUrl) {
        self.callback({
          level: 'info',
          msg: `Navigation: ${newUrl}`,
          ts: new Date().toISOString(),
          source: 'browser',
          meta: {
            type: 'navigation',
            trigger: 'pushState',
            from: self.lastUrl,
            to: newUrl,
            url: newUrl
          }
        });
        self.lastUrl = newUrl;
      }
      
      return result;
    };

    History.prototype.replaceState = function(...args) {
      const result = self.originalReplaceState.apply(this, args);
      const newUrl = window.location.href;
      
      if (self.callback && newUrl !== self.lastUrl) {
        self.callback({
          level: 'info',
          msg: `Navigation (replace): ${newUrl}`,
          ts: new Date().toISOString(),
          source: 'browser',
          meta: {
            type: 'navigation',
            trigger: 'replaceState',
            from: self.lastUrl,
            to: newUrl,
            url: newUrl
          }
        });
        self.lastUrl = newUrl;
      }
      
      return result;
    };
  }

  private restoreHistory(): void {
    History.prototype.pushState = this.originalPushState;
    History.prototype.replaceState = this.originalReplaceState;
  }

  private setupListeners(): void {
    window.addEventListener('popstate', this.handlePopState);
    window.addEventListener('hashchange', this.handleHashChange);
    window.addEventListener('load', this.handleLoad);
  }

  private removeListeners(): void {
    window.removeEventListener('popstate', this.handlePopState);
    window.removeEventListener('hashchange', this.handleHashChange);
    window.removeEventListener('load', this.handleLoad);
  }

  private handlePopState = (): void => {
    const newUrl = window.location.href;
    
    if (this.callback && newUrl !== this.lastUrl) {
      this.callback({
        level: 'info',
        msg: `Navigation (back/forward): ${newUrl}`,
        ts: new Date().toISOString(),
        source: 'browser',
        meta: {
          type: 'navigation',
          trigger: 'popstate',
          from: this.lastUrl,
          to: newUrl,
          url: newUrl
        }
      });
      this.lastUrl = newUrl;
    }
  };

  private handleHashChange = (): void => {
    const newUrl = window.location.href;
    
    if (this.callback && newUrl !== this.lastUrl) {
      this.callback({
        level: 'info',
        msg: `Hash change: ${window.location.hash}`,
        ts: new Date().toISOString(),
        source: 'browser',
        meta: {
          type: 'navigation',
          trigger: 'hashchange',
          from: this.lastUrl,
          to: newUrl,
          url: newUrl
        }
      });
      this.lastUrl = newUrl;
    }
  };

  private handleLoad = (): void => {
    if (this.callback && this.lastUrl !== window.location.href) {
      this.callback({
        level: 'info',
        msg: `Page loaded: ${window.location.href}`,
        ts: new Date().toISOString(),
        source: 'browser',
        meta: {
          type: 'navigation',
          trigger: 'load',
          to: window.location.href,
          url: window.location.href
        }
      });
      this.lastUrl = window.location.href;
    }
  };
}